import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { MainLayout } from '../../components/layout/MainLayout';
import { returnService, ReturnAuthorization, ReturnPart, ReturnComment, ReturnImage } from '../../api/services/returnService';
import { orderNoteService, OrderNote } from '../../api/services/orderNoteService';
import { toast } from 'react-hot-toast';
import { Printer, Upload, X, Image as ImageIcon } from 'lucide-react';

interface ReturnWithDetails extends ReturnAuthorization {
  parts: ReturnPart[];
  comments: ReturnComment[];
  images?: ReturnImage[];
}

// Print styles to fit on one page
const printStyles = `
  @media print {
    @page {
      size: letter;
      margin: 0.3in;
    }

    /* Force light mode colors */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    body {
      background: white !important;
      color: #111 !important;
    }

    /* Hide non-printable elements */
    .no-print,
    .comments-section,
    nav,
    aside,
    .sidebar,
    button:not(.print-show),
    textarea,
    input,
    .add-comment-form {
      display: none !important;
    }

    /* Reset layout */
    .print-container {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      padding: 0 !important;
      margin: 0 !important;
      background: white !important;
      overflow: visible !important;
    }

    .print-content {
      max-width: 100% !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    /* Compact styling - reduced margins */
    .print-section {
      background: white !important;
      color: #111 !important;
      box-shadow: none !important;
      border: 1px solid #d1d5db !important;
      margin-bottom: 6px !important;
      padding: 6px 10px !important;
      border-radius: 3px !important;
      page-break-inside: avoid !important;
    }

    .print-section h2,
    .print-section h3 {
      font-size: 11px !important;
      margin-bottom: 3px !important;
      color: #111 !important;
    }

    .print-header {
      display: flex !important;
      justify-content: space-between !important;
      align-items: center !important;
      border: none !important;
      border-bottom: 2px solid #374151 !important;
      padding: 4px 0 6px 0 !important;
      margin-bottom: 8px !important;
      background: white !important;
    }

    .print-header h1 {
      font-size: 14px !important;
      font-weight: bold !important;
      color: #111 !important;
    }

    /* Force light mode on dark mode elements */
    .dark\\:bg-gray-800,
    .dark\\:bg-gray-900,
    .dark\\:bg-gray-700,
    .dark\\:bg-gray-900\\/50 {
      background: white !important;
    }

    .dark\\:text-white,
    .dark\\:text-gray-100,
    .dark\\:text-gray-200,
    .dark\\:text-gray-300,
    .dark\\:text-gray-400 {
      color: #111 !important;
    }

    .print-grid {
      display: grid !important;
      grid-template-columns: 1fr 1fr !important;
      gap: 4px 16px !important;
    }

    .print-grid > div {
      font-size: 10px !important;
      line-height: 1.3 !important;
      color: #111 !important;
    }

    .print-grid span {
      color: #111 !important;
    }

    .print-address {
      font-size: 10px !important;
      line-height: 1.3 !important;
      padding: 4px !important;
      background: #f9fafb !important;
      color: #111 !important;
    }

    .print-reason {
      font-size: 10px !important;
      line-height: 1.3 !important;
      padding: 4px !important;
      background: #f9fafb !important;
      color: #111 !important;
      max-height: 80px !important;
      overflow: hidden !important;
    }

    /* Compact table */
    .print-table {
      width: 100% !important;
      border-collapse: collapse !important;
      font-size: 9px !important;
    }

    .print-table th,
    .print-table td {
      padding: 3px 5px !important;
      border: 1px solid #d1d5db !important;
      text-align: left !important;
      color: #111 !important;
    }

    .print-table th {
      background: #f3f4f6 !important;
      font-weight: 600 !important;
      color: #111 !important;
    }

    /* Contact info inline */
    .print-contact {
      display: flex !important;
      gap: 16px !important;
      font-size: 10px !important;
    }

    .print-contact span {
      color: #111 !important;
    }

    /* Status badge for print */
    .print-status {
      padding: 2px 6px !important;
      border-radius: 3px !important;
      font-size: 9px !important;
      font-weight: 600 !important;
      border: 1px solid #d1d5db !important;
    }
  }
`;

const ReturnDetail: React.FC = () => {
  const { returnId } = useParams<{ returnId: string }>();
  const navigate = useNavigate();
  const [returnData, setReturnData] = useState<ReturnWithDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [newComment, setNewComment] = useState<string>('');
  const [submittingComment, setSubmittingComment] = useState<boolean>(false);
  const [markingReceived] = useState<boolean>(false);
  const [markingCompleted, setMarkingCompleted] = useState<boolean>(false);
  const [isEditingDescription, setIsEditingDescription] = useState<boolean>(false);
  const [editedDescription, setEditedDescription] = useState<string>('');
  const [savingDescription, setSavingDescription] = useState<boolean>(false);
  const [isEditingCreditMemo, setIsEditingCreditMemo] = useState<boolean>(false);
  const [editedCreditMemo, setEditedCreditMemo] = useState<string>('');
  const [savingCreditMemo, setSavingCreditMemo] = useState<boolean>(false);
  const [isEditingShipTo, setIsEditingShipTo] = useState<boolean>(false);
  const [savingShipTo, setSavingShipTo] = useState<boolean>(false);
  const emptyShipTo = {
    ShipToName: '',
    ShipToAddress1: '',
    ShipToAddress2: '',
    ShipToCity: '',
    ShipToState: '',
    ShipToZipCode: '',
    ShipToCountry: '',
  };
  const [editedShipTo, setEditedShipTo] = useState(emptyShipTo);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editedCommentText, setEditedCommentText] = useState<string>('');
  const [savingComment, setSavingComment] = useState<boolean>(false);
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);
  const [startingRepair, setStartingRepair] = useState<boolean>(false);

  // Ship modal state
  const [showShipModal, setShowShipModal] = useState<boolean>(false);
  const [shipFormData, setShipFormData] = useState({
    shippingCompany: '',
    trackingNumber: '',
    comments: '',
    sendCustomerEmail: true
  });
  const [submittingShipment, setSubmittingShipment] = useState<boolean>(false);

  // Part editing state - multi-select mode
  const [isEditingParts, setIsEditingParts] = useState<boolean>(false);
  const [selectedPartNumbers, setSelectedPartNumbers] = useState<string[]>([]);
  const [orderItems, setOrderItems] = useState<{ PartNumber: string; Description: string }[]>([]);
  const [loadingOrderItems, setLoadingOrderItems] = useState<boolean>(false);
  const [savingParts, setSavingParts] = useState<boolean>(false);

  // Return type editing state
  const [isEditingType, setIsEditingType] = useState<boolean>(false);
  const [editedReturnType, setEditedReturnType] = useState<number>(0);
  const [savingType, setSavingType] = useState<boolean>(false);

  // Image upload state
  const [images, setImages] = useState<ReturnImage[]>([]);
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);
  const [deletingImageId, setDeletingImageId] = useState<number | null>(null);
  const [selectedImageForPreview, setSelectedImageForPreview] = useState<ReturnImage | null>(null);

  // Contact info editing state
  const [isEditingContact, setIsEditingContact] = useState<boolean>(false);
  const [editedContact, setEditedContact] = useState<{ recipientName: string; emailAddress: string; phoneNumber: string }>({
    recipientName: '',
    emailAddress: '',
    phoneNumber: ''
  });
  const [savingContact, setSavingContact] = useState<boolean>(false);

  // Mark as Received modal state
  const [showReceivedModal, setShowReceivedModal] = useState<boolean>(false);
  const [receivedCondition, setReceivedCondition] = useState<'passed' | 'not_sellable' | ''>('');
  const [binNumber, setBinNumber] = useState<string>('');
  const [notSellableNote, setNotSellableNote] = useState<string>('');
  const [processingReceived, setProcessingReceived] = useState<boolean>(false);

  // Original order notes state
  const [orderNotes, setOrderNotes] = useState<OrderNote[]>([]);
  const [loadingOrderNotes, setLoadingOrderNotes] = useState<boolean>(false);

  useEffect(() => {
    loadReturnDetail();
    loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnId]);

  const loadImages = async () => {
    if (!returnId) return;

    setLoadingImages(true);
    try {
      const result = await returnService.getReturnImages(parseInt(returnId));
      setImages(result);
    } catch (error) {
      console.error('Error loading images:', error);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !returnId) return;

    const file = files[0];
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const uploadedBy = user.UserName || 'system';

    setUploadingImage(true);
    try {
      await returnService.uploadImage(parseInt(returnId), file, uploadedBy);
      await loadImages();
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    const confirmed = window.confirm('Are you sure you want to delete this image?');
    if (!confirmed) return;

    setDeletingImageId(imageId);
    try {
      await returnService.deleteImage(imageId);
      await loadImages();
      toast.success('Image deleted successfully');
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    } finally {
      setDeletingImageId(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const loadReturnDetail = async () => {
    if (!returnId) return;

    setLoading(true);
    try {
      const result = await returnService.getReturnById(parseInt(returnId));
      setReturnData(result);
      // Load order notes after getting return data
      if (result.OrderId) {
        loadOrderNotes(result.OrderId);
      }
    } catch (error) {
      console.error('Error loading return detail:', error);
      toast.error('Failed to load return details');
    } finally {
      setLoading(false);
    }
  };

  const loadOrderNotes = async (orderId: number) => {
    setLoadingOrderNotes(true);
    try {
      const notes = await orderNoteService.getOrderNotes(orderId);
      setOrderNotes(notes);
    } catch (error) {
      console.error('Error loading order notes:', error);
    } finally {
      setLoadingOrderNotes(false);
    }
  };

  const handleAddComment = async () => {
    if (!returnId || !newComment.trim()) return;

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const createdBy = user.UserName || 'system';

    setSubmittingComment(true);
    try {
      await returnService.addComment(parseInt(returnId), newComment.trim(), createdBy);
      setNewComment('');
      await loadReturnDetail();
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEditComment = (comment: ReturnComment) => {
    setEditingCommentId(comment.Id);
    setEditedCommentText(comment.Description);
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditedCommentText('');
  };

  const handleSaveComment = async () => {
    if (!editingCommentId || !editedCommentText.trim()) return;

    setSavingComment(true);
    try {
      await returnService.updateComment(editingCommentId, editedCommentText.trim());
      await loadReturnDetail();
      setEditingCommentId(null);
      setEditedCommentText('');
      toast.success('Comment updated successfully');
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    } finally {
      setSavingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    const confirmed = window.confirm('Are you sure you want to delete this comment?');
    if (!confirmed) return;

    setDeletingCommentId(commentId);
    try {
      await returnService.deleteComment(commentId);
      await loadReturnDetail();
      toast.success('Comment deleted successfully');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleMarkReceived = () => {
    // Reset modal state and show modal
    setReceivedCondition('');
    setBinNumber('');
    setNotSellableNote('');
    setShowReceivedModal(true);
  };

  const handleCloseReceivedModal = () => {
    setShowReceivedModal(false);
    setReceivedCondition('');
    setBinNumber('');
    setNotSellableNote('');
  };

  const handleSubmitReceived = async () => {
    if (!returnId || !receivedCondition) return;

    // Validate based on condition
    if (receivedCondition === 'passed' && !binNumber.trim()) {
      toast.error('Please enter a bin number');
      return;
    }

    if (receivedCondition === 'not_sellable' && !notSellableNote.trim()) {
      toast.error('Please enter a note explaining why the item is not sellable');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const receivedBy = user.UserName || 'system';

    setProcessingReceived(true);
    try {
      await returnService.processReceived(parseInt(returnId), {
        condition: receivedCondition,
        binNumber: receivedCondition === 'passed' ? binNumber.trim() : undefined,
        note: receivedCondition === 'not_sellable' ? notSellableNote.trim() : undefined,
        receivedBy
      });
      await loadReturnDetail();
      handleCloseReceivedModal();
      toast.success(
        receivedCondition === 'passed'
          ? 'Return processed: parts added to stock and return closed'
          : 'Return processed: marked as not sellable and return closed'
      );
    } catch (error) {
      console.error('Error processing received:', error);
      toast.error('Failed to process return');
    } finally {
      setProcessingReceived(false);
    }
  };

  const handleMarkCompleted = async () => {
    if (!returnId) return;

    const confirmed = window.confirm('Mark this return as completed?');
    if (!confirmed) return;

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const completedBy = user.UserName || 'system';

    setMarkingCompleted(true);
    try {
      await returnService.markAsCompleted(parseInt(returnId), completedBy);
      await loadReturnDetail();
      toast.success('Return marked as completed');
    } catch (error) {
      console.error('Error marking as completed:', error);
      toast.error('Failed to mark as completed');
    } finally {
      setMarkingCompleted(false);
    }
  };

  const handleEditShipTo = () => {
    setEditedShipTo({
      ShipToName: returnData?.ShipToName || '',
      ShipToAddress1: returnData?.ShipToAddress1 || '',
      ShipToAddress2: returnData?.ShipToAddress2 || '',
      ShipToCity: returnData?.ShipToCity || '',
      ShipToState: returnData?.ShipToState || '',
      ShipToZipCode: returnData?.ShipToZipCode || '',
      ShipToCountry: returnData?.ShipToCountry || '',
    });
    setIsEditingShipTo(true);
  };

  const handleSaveShipTo = async () => {
    if (!returnId) return;
    const trimmed = Object.fromEntries(
      Object.entries(editedShipTo).map(([k, v]) => [k, v.trim()])
    ) as typeof editedShipTo;
    if (!trimmed.ShipToName || !trimmed.ShipToAddress1) {
      toast.error('Name and Address 1 are required');
      return;
    }
    setSavingShipTo(true);
    try {
      await returnService.updateShipTo(parseInt(returnId), trimmed);
      setReturnData(prev => prev ? { ...prev, ...trimmed } : prev);
      setIsEditingShipTo(false);
      toast.success('Ship-to address updated');
    } catch (error) {
      console.error('Error updating ship-to address:', error);
      toast.error('Failed to update ship-to address');
    } finally {
      setSavingShipTo(false);
    }
  };

  const handleEditCreditMemo = () => {
    setEditedCreditMemo(returnData?.CreditMemo || '');
    setIsEditingCreditMemo(true);
  };

  const handleCancelEditCreditMemo = () => {
    setIsEditingCreditMemo(false);
    setEditedCreditMemo('');
  };

  const handleSaveCreditMemo = async () => {
    if (!returnId) return;
    setSavingCreditMemo(true);
    try {
      await returnService.updateCreditMemo(parseInt(returnId), editedCreditMemo.trim());
      setReturnData(prev => prev ? { ...prev, CreditMemo: editedCreditMemo.trim() } : prev);
      setIsEditingCreditMemo(false);
      toast.success('Credit memo updated');
    } catch (error) {
      console.error('Error updating credit memo:', error);
      toast.error('Failed to update credit memo');
    } finally {
      setSavingCreditMemo(false);
    }
  };

  const handleEditDescription = () => {
    setEditedDescription(returnData?.Description || '');
    setIsEditingDescription(true);
  };

  const handleCancelEditDescription = () => {
    setIsEditingDescription(false);
    setEditedDescription('');
  };

  const handleSaveDescription = async () => {
    if (!returnId || !editedDescription.trim()) return;

    setSavingDescription(true);
    try {
      await returnService.updateDescription(parseInt(returnId), editedDescription.trim());
      await loadReturnDetail();
      setIsEditingDescription(false);
      toast.success('Reason updated successfully');
    } catch (error) {
      console.error('Error updating description:', error);
      toast.error('Failed to update reason');
    } finally {
      setSavingDescription(false);
    }
  };

  const handleDelete = async () => {
    if (!returnId || !returnData) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete this ${getReturnTypeName(returnData.ReturnType).toLowerCase()} authorization?\n\nAuthorization #: ${returnData.AuthorizationNumber}\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    setDeleting(true);
    try {
      await returnService.deleteReturn(parseInt(returnId));
      toast.success('Return authorization deleted successfully');
      // Navigate back to the order detail page
      navigate(`/production/orders/${returnData.OrderId}`);
    } catch (error) {
      console.error('Error deleting return:', error);
      toast.error('Failed to delete return authorization');
      setDeleting(false);
    }
  };

  const handleStartRepair = async () => {
    if (!returnId || !returnData) return;

    if (!returnData.EmailAddress) {
      toast.error('No customer email address on file. Cannot send notification.');
      return;
    }

    const confirmed = window.confirm(
      `Start repair and send notification email to ${returnData.EmailAddress}?`
    );
    if (!confirmed) return;

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const startedBy = user.UserName || 'system';

    setStartingRepair(true);
    try {
      await returnService.startRepair(parseInt(returnId), startedBy);
      toast.success('Repair started and notification sent to customer');
      await loadReturnDetail(); // Reload to update the status
    } catch (error) {
      console.error('Error starting repair:', error);
      toast.error('Failed to start repair');
    } finally {
      setStartingRepair(false);
    }
  };

  // Check if repair has been started (RepairComments starts with "STARTED|")
  const isRepairStarted = returnData?.RepairComments?.startsWith('STARTED|') || false;

  const handleOpenShipModal = () => {
    setShipFormData({
      shippingCompany: '',
      trackingNumber: '',
      comments: '',
      sendCustomerEmail: true
    });
    setShowShipModal(true);
  };

  const handleCloseShipModal = () => {
    setShowShipModal(false);
  };

  const handleShipFormChange = (field: string, value: string | boolean) => {
    setShipFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitShipment = async () => {
    if (!returnId || !returnData) return;

    if (!shipFormData.shippingCompany) {
      toast.error('Please select a shipping company');
      return;
    }

    if (!shipFormData.trackingNumber.trim()) {
      toast.error('Please enter a tracking number');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const completedBy = user.UserName || 'system';

    setSubmittingShipment(true);
    try {
      await returnService.shipRepair(parseInt(returnId), {
        shippingCompany: shipFormData.shippingCompany,
        trackingNumber: shipFormData.trackingNumber.trim(),
        comments: shipFormData.comments.trim(),
        sendCustomerEmail: shipFormData.sendCustomerEmail,
        completedBy
      });
      toast.success('Shipment recorded and repair marked as completed');
      setShowShipModal(false);
      await loadReturnDetail();
    } catch (error) {
      console.error('Error submitting shipment:', error);
      toast.error('Failed to record shipment');
    } finally {
      setSubmittingShipment(false);
    }
  };

  const getReturnTypeName = (type: number) => {
    return type === 1 ? 'Repair' : 'Return';
  };

  const handleEditType = () => {
    if (returnData) {
      setEditedReturnType(returnData.ReturnType);
      setIsEditingType(true);
    }
  };

  const handleCancelEditType = () => {
    setIsEditingType(false);
    setEditedReturnType(0);
  };

  const handleSaveType = async () => {
    if (!returnId || !editedReturnType) return;

    setSavingType(true);
    try {
      await returnService.updateReturnType(parseInt(returnId), editedReturnType);
      await loadReturnDetail();
      setIsEditingType(false);
      setEditedReturnType(0);
      toast.success('Return type updated successfully');
    } catch (error) {
      console.error('Error updating return type:', error);
      toast.error('Failed to update return type');
    } finally {
      setSavingType(false);
    }
  };

  const handleEditContact = () => {
    if (returnData) {
      setEditedContact({
        recipientName: returnData.RecipientName || '',
        emailAddress: returnData.EmailAddress || '',
        phoneNumber: returnData.PhoneNumber || ''
      });
      setIsEditingContact(true);
    }
  };

  const handleCancelEditContact = () => {
    setIsEditingContact(false);
    setEditedContact({ recipientName: '', emailAddress: '', phoneNumber: '' });
  };

  const handleSaveContact = async () => {
    if (!returnId) return;

    setSavingContact(true);
    try {
      await returnService.updateContactInfo(parseInt(returnId), editedContact);
      await loadReturnDetail();
      setIsEditingContact(false);
      setEditedContact({ recipientName: '', emailAddress: '', phoneNumber: '' });
      toast.success('Contact information updated successfully');
    } catch (error) {
      console.error('Error updating contact info:', error);
      toast.error('Failed to update contact information');
    } finally {
      setSavingContact(false);
    }
  };

  const handleContactChange = (field: keyof typeof editedContact, value: string) => {
    setEditedContact(prev => ({ ...prev, [field]: value }));
  };

  const handleEditParts = async () => {
    // Initialize selected parts from current return parts
    if (returnData) {
      setSelectedPartNumbers(returnData.parts.map(p => p.PartNumber));
    }
    setIsEditingParts(true);

    // Load order items if not already loaded
    if (orderItems.length === 0 && returnId) {
      setLoadingOrderItems(true);
      try {
        const items = await returnService.getOrderItems(parseInt(returnId));
        setOrderItems(items);
      } catch (error) {
        console.error('Error loading order items:', error);
        toast.error('Failed to load order items');
      } finally {
        setLoadingOrderItems(false);
      }
    }
  };

  const handleCancelEditParts = () => {
    setIsEditingParts(false);
    setSelectedPartNumbers([]);
  };

  const handleTogglePartNumber = (partNumber: string) => {
    setSelectedPartNumbers(prev => {
      if (prev.includes(partNumber)) {
        return prev.filter(pn => pn !== partNumber);
      } else {
        return [...prev, partNumber];
      }
    });
  };

  const handleSaveParts = async () => {
    if (!returnId || selectedPartNumbers.length === 0) {
      toast.error('Please select at least one part');
      return;
    }

    setSavingParts(true);
    try {
      await returnService.bulkUpdateParts(parseInt(returnId), selectedPartNumbers);
      await loadReturnDetail();
      setIsEditingParts(false);
      setSelectedPartNumbers([]);
      toast.success('Parts updated successfully');
    } catch (error) {
      console.error('Error updating parts:', error);
      toast.error('Failed to update parts');
    } finally {
      setSavingParts(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePrintPackingSlip = () => {
    if (!returnData) return;

    const packingSlipContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Packing Slip - ${returnData.AuthorizationNumber}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 8.5in;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
            margin-bottom: 20px;
          }
          .header h1 {
            font-size: 24px;
            margin-bottom: 5px;
          }
          .header .auth-number {
            font-size: 16px;
            color: #555;
          }
          .header .type-badge {
            display: inline-block;
            padding: 4px 12px;
            background: #e0e0e0;
            border-radius: 4px;
            font-weight: bold;
            margin-top: 8px;
          }
          .section {
            margin-bottom: 20px;
          }
          .section-title {
            font-size: 14px;
            font-weight: bold;
            background: #f0f0f0;
            padding: 8px 12px;
            margin-bottom: 10px;
            border-left: 4px solid #333;
          }
          .ship-to {
            padding: 15px;
            border: 1px solid #ddd;
            background: #fafafa;
          }
          .ship-to .name {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .ship-to .address {
            line-height: 1.5;
          }
          .reason {
            padding: 15px;
            border: 1px solid #ddd;
            background: #fff9e6;
            line-height: 1.6;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 10px;
            text-align: left;
          }
          th {
            background: #f0f0f0;
            font-weight: bold;
          }
          .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 12px;
            color: #666;
          }
          @media print {
            body { padding: 0; }
            @page { margin: 0.5in; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>PACKING SLIP</h1>
          <div class="auth-number">Authorization #: ${returnData.AuthorizationNumber}</div>
          <div class="type-badge">${returnData.ReturnType === 1 ? 'REPAIR' : 'RETURN'}</div>
        </div>

        <div class="section">
          <div class="section-title">SHIP TO</div>
          <div class="ship-to">
            ${returnData.ShipToName ? `<div class="name">${returnData.ShipToName}</div>` : ''}
            <div class="address">
              ${returnData.ShipToAddress1 ? `${returnData.ShipToAddress1}<br>` : ''}
              ${returnData.ShipToAddress2 ? `${returnData.ShipToAddress2}<br>` : ''}
              ${[returnData.ShipToCity, returnData.ShipToState, returnData.ShipToZipCode].filter(Boolean).join(', ')}
              ${returnData.ShipToCountry && returnData.ShipToCountry !== 'US' && returnData.ShipToCountry !== 'USA' ? `<br>${returnData.ShipToCountry}` : ''}
            </div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">RETURN/REPAIR REASON</div>
          <div class="reason">
            ${returnData.Description || 'No reason provided.'}
          </div>
        </div>

        <div class="section">
          <div class="section-title">PARTS</div>
          <table>
            <thead>
              <tr>
                <th style="width: 30%">Part Number</th>
                <th>Description</th>
                <th style="width: 15%">Type</th>
              </tr>
            </thead>
            <tbody>
              ${returnData.parts.length === 0
                ? '<tr><td colspan="3" style="text-align: center; color: #666;">No parts listed</td></tr>'
                : returnData.parts.map(part => `
                  <tr>
                    <td><strong>${part.PartNumber}</strong></td>
                    <td>${part.Description || '-'}</td>
                    <td>${part.IsRepair ? 'Repair' : 'Return'}</td>
                  </tr>
                `).join('')
              }
            </tbody>
          </table>
        </div>

        <div class="footer">
          <p>Order ID: ${returnData.OrderId} | Printed: ${format(new Date(), 'M/d/yyyy h:mm a')}</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(packingSlipContent);
      printWindow.document.close();
    }
  };

  const getStatusBadge = () => {
    if (!returnData) return null;

    if (returnData.CompleteDate) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          Completed
        </span>
      );
    } else if (returnData.ReceiveDate) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          Received
        </span>
      );
    } else if (isRepairStarted) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
          In Progress
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
          Pending
        </span>
      );
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading return details...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!returnData) {
    return (
      <MainLayout>
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Return Not Found</h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">The requested return could not be found.</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Inject print styles */}
      <style>{printStyles}</style>

      <div className="flex-1 bg-gray-100 dark:bg-gray-900 overflow-auto print-container">
        <div className="max-w-5xl mx-auto px-6 py-6 print-content">
          {/* Header */}
          <div className="bg-gray-200 dark:bg-gray-800 px-6 py-4 mb-6 rounded-t-lg flex justify-between items-center print-header print-section">
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {getReturnTypeName(returnData.ReturnType)} Authorization
            </h1>
            {getStatusBadge()}
          </div>

          {/* Back Button and Actions */}
          <div className="flex justify-between items-center mb-6 no-print">
            <button
              onClick={() => navigate(`/production/orders/${returnData.OrderId}`)}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
            >
              Back to Order
            </button>
            <div className="flex gap-2">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center gap-2"
              >
                <Printer size={16} />
                Print
              </button>
              <button
                onClick={handlePrintPackingSlip}
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-2"
              >
                <Printer size={16} />
                Packing Slip
              </button>
              {returnData.ReturnType === 1 && !returnData.CompleteDate && (
                <button
                  onClick={handleStartRepair}
                  disabled={startingRepair || isRepairStarted}
                  className={`px-4 py-2 text-white rounded ${
                    isRepairStarted
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 disabled:bg-green-400'
                  }`}
                >
                  {startingRepair ? 'Starting...' : isRepairStarted ? 'Repair Started' : 'Start Repair'}
                </button>
              )}
              {returnData.ReturnType === 1 && isRepairStarted && !returnData.CompleteDate && (
                <button
                  onClick={handleOpenShipModal}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Ship Repair/Replacement
                </button>
              )}
              {!returnData.ReceiveDate && returnData.ReturnType !== 1 && (
                <button
                  onClick={handleMarkReceived}
                  disabled={markingReceived}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
                >
                  {markingReceived ? 'Marking...' : 'Mark as Received'}
                </button>
              )}
              {returnData.ReceiveDate && !returnData.CompleteDate && (
                <button
                  onClick={handleMarkCompleted}
                  disabled={markingCompleted}
                  className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:bg-emerald-400"
                >
                  {markingCompleted ? 'Marking...' : 'Mark as Completed'}
                </button>
              )}
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-red-400"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>

          {/* Authorization Details */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 print-section">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Authorization #: {returnData.AuthorizationNumber}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 print-grid">
              <div className="space-y-3">
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Order ID:</span>
                  <span className="ml-2 text-blue-600 dark:text-blue-400 cursor-pointer hover:underline"
                    onClick={() => navigate(`/production/orders/${returnData.OrderId}`)}
                  >
                    {returnData.OrderId}
                  </span>
                </div>
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Type:</span>
                  {isEditingType ? (
                    <span className="ml-2 inline-flex items-center gap-2 no-print">
                      <select
                        value={editedReturnType}
                        onChange={(e) => setEditedReturnType(parseInt(e.target.value))}
                        className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      >
                        <option value={1}>Repair</option>
                        <option value={2}>Return</option>
                      </select>
                      <button
                        onClick={handleSaveType}
                        disabled={savingType}
                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                      >
                        {savingType ? '...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEditType}
                        disabled={savingType}
                        className="px-2 py-1 text-xs bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <span className="ml-2">
                      <span className="text-gray-700 dark:text-gray-300">{getReturnTypeName(returnData.ReturnType)}</span>
                      <button
                        onClick={handleEditType}
                        className="ml-2 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 no-print"
                      >
                        Edit
                      </button>
                    </span>
                  )}
                </div>
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Tag:</span>
                  <span className="ml-2 text-gray-700 dark:text-gray-300">{returnData.ReturnTag || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Free Shipping Labels:</span>
                  <span className="ml-2 text-gray-700 dark:text-gray-300">{returnData.HasReturnPackage ? 'Yes' : 'No'}</span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Created:</span>
                  <span className="ml-2 text-gray-700 dark:text-gray-300">
                    {format(new Date(returnData.CreateDate), 'M/d/yyyy h:mm a')} by {returnData.CreateBy}
                  </span>
                </div>
                {returnData.ReceiveDate && (
                  <div>
                    <span className="font-semibold text-gray-900 dark:text-white">Received:</span>
                    <span className="ml-2 text-gray-700 dark:text-gray-300">
                      {format(new Date(returnData.ReceiveDate), 'M/d/yyyy h:mm a')} by {returnData.ReceiveBy}
                    </span>
                  </div>
                )}
                {returnData.CompleteDate && (
                  <div>
                    <span className="font-semibold text-gray-900 dark:text-white">Completed:</span>
                    <span className="ml-2 text-gray-700 dark:text-gray-300">
                      {format(new Date(returnData.CompleteDate), 'M/d/yyyy h:mm a')} by {returnData.CompleteBy}
                    </span>
                  </div>
                )}
                {returnData.ReturnBinNumber && (
                  <div>
                    <span className="font-semibold text-gray-900 dark:text-white">Bin Number:</span>
                    <span className="ml-2 text-gray-700 dark:text-gray-300">{returnData.ReturnBinNumber}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Credit Memo */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 print-section">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">Credit Memo Number</h3>
              {!isEditingCreditMemo && (
                <button
                  onClick={handleEditCreditMemo}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 no-print"
                >
                  Edit
                </button>
              )}
            </div>
            {isEditingCreditMemo ? (
              <div className="no-print">
                <input
                  type="text"
                  value={editedCreditMemo}
                  onChange={(e) => setEditedCreditMemo(e.target.value)}
                  maxLength={50}
                  placeholder="e.g. CM773459"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleSaveCreditMemo}
                    disabled={savingCreditMemo}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                  >
                    {savingCreditMemo ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEditCreditMemo}
                    disabled={savingCreditMemo}
                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded text-gray-700 dark:text-gray-300 font-mono">
                {returnData.CreditMemo || <span className="italic text-gray-400 dark:text-gray-500 font-sans">No credit memo number assigned.</span>}
              </div>
            )}
          </div>

          {/* Bill-To and Ship-To Addresses */}
          {(

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 print-section">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Bill-To Address - Left */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Bill-To Address</h3>
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded text-gray-700 dark:text-gray-300 print-address">
                    {returnData.BillToName ? (
                      <>
                        <div className="font-semibold">{returnData.BillToName}</div>
                        {returnData.BillToAddress1 && (
                          <div>{returnData.BillToAddress1}</div>
                        )}
                        {returnData.BillToAddress2 && (
                          <div>{returnData.BillToAddress2}</div>
                        )}
                        {(returnData.BillToCity || returnData.BillToState || returnData.BillToZipCode) && (
                          <div>
                            {[
                              returnData.BillToCity,
                              returnData.BillToState,
                              returnData.BillToZipCode
                            ].filter(Boolean).join(', ')}
                          </div>
                        )}
                        {returnData.BillToCountry && returnData.BillToCountry !== 'US' && returnData.BillToCountry !== 'USA' && (
                          <div>{returnData.BillToCountry}</div>
                        )}
                      </>
                    ) : (
                      <div className="text-gray-500 dark:text-gray-400 italic">No bill-to address on file</div>
                    )}
                  </div>
                </div>

                {/* Ship-To Address - Right */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white">Ship-To Address</h3>
                    {!isEditingShipTo && (
                      <button
                        onClick={handleEditShipTo}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 no-print"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {isEditingShipTo ? (
                    <div className="no-print space-y-2">
                      {([
                        ['ShipToName', 'Name'],
                        ['ShipToAddress1', 'Address 1'],
                        ['ShipToAddress2', 'Address 2'],
                        ['ShipToCity', 'City'],
                        ['ShipToState', 'State'],
                        ['ShipToZipCode', 'Zip Code'],
                        ['ShipToCountry', 'Country'],
                      ] as const).map(([field, label]) => (
                        <input
                          key={field}
                          type="text"
                          value={editedShipTo[field]}
                          onChange={(e) => setEditedShipTo(prev => ({ ...prev, [field]: e.target.value }))}
                          placeholder={label}
                          aria-label={label}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      ))}
                      <div className="flex gap-2 pt-1">
                        <button
                          onClick={handleSaveShipTo}
                          disabled={savingShipTo}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                        >
                          {savingShipTo ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={() => setIsEditingShipTo(false)}
                          disabled={savingShipTo}
                          className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded text-gray-700 dark:text-gray-300 print-address">
                    {returnData.ShipToName ? (
                      <>
                        <div className="font-semibold">{returnData.ShipToName}</div>
                        {returnData.ShipToAddress1 && (
                          <div>{returnData.ShipToAddress1}</div>
                        )}
                        {returnData.ShipToAddress2 && (
                          <div>{returnData.ShipToAddress2}</div>
                        )}
                        {(returnData.ShipToCity || returnData.ShipToState || returnData.ShipToZipCode) && (
                          <div>
                            {[
                              returnData.ShipToCity,
                              returnData.ShipToState,
                              returnData.ShipToZipCode
                            ].filter(Boolean).join(', ')}
                          </div>
                        )}
                        {returnData.ShipToCountry && returnData.ShipToCountry !== 'US' && returnData.ShipToCountry !== 'USA' && (
                          <div>{returnData.ShipToCountry}</div>
                        )}
                      </>
                    ) : (
                      <div className="text-gray-500 dark:text-gray-400 italic">No ship-to address on file</div>
                    )}
                  </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 print-section">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">Return/Repair Reason</h3>
              {!isEditingDescription && (
                <button
                  onClick={handleEditDescription}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 no-print"
                >
                  Edit
                </button>
              )}
            </div>
            {isEditingDescription ? (
              <div className="no-print">
                <textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter return/repair reason..."
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleSaveDescription}
                    disabled={savingDescription || !editedDescription.trim()}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                  >
                    {savingDescription ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEditDescription}
                    disabled={savingDescription}
                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded text-gray-700 dark:text-gray-300 print-reason">
                {returnData.Description || 'No description provided.'}
              </div>
            )}
          </div>

          {/* Repair Comments (if any) - hidden on print since it shows internal status */}
          {returnData.RepairComments && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 no-print">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Repair Comments</h3>
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded text-gray-700 dark:text-gray-300">
                {returnData.RepairComments}
              </div>
            </div>
          )}

          {/* Original Order Notes */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 print-section">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-gray-900 dark:text-white">Original Order Notes</h3>
              <button
                onClick={() => navigate(`/production/orders/${returnData.OrderId}`)}
                className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 no-print"
              >
                View Order
              </button>
            </div>
            {loadingOrderNotes ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading notes...</p>
              </div>
            ) : orderNotes.length === 0 ? (
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded text-gray-500 dark:text-gray-400 text-center">
                No notes on the original order.
              </div>
            ) : (
              <div className="space-y-3">
                {orderNotes.map((note) => (
                  <div
                    key={note.Id}
                    className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded border-l-4 border-blue-500"
                  >
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{note.Note}</p>
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      By {note.CreateBy} on {format(new Date(note.CreateDate), 'M/d/yyyy h:mm a')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Parts */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 overflow-hidden print-section">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900 dark:text-white">Parts</h3>
              {!isEditingParts && (
                <button
                  onClick={handleEditParts}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 no-print"
                >
                  Edit Parts
                </button>
              )}
            </div>

            {/* Edit Parts Mode - Multi-select */}
            {isEditingParts && (
              <div className="px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700 no-print">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Select the part numbers to associate with this return:
                </p>
                {loadingOrderItems ? (
                  <div className="text-gray-500">Loading order items...</div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {orderItems.map((item) => (
                      <label
                        key={item.PartNumber}
                        className="flex items-center gap-3 p-2 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedPartNumbers.includes(item.PartNumber)}
                          onChange={() => handleTogglePartNumber(item.PartNumber)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {item.PartNumber}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          - {item.Description}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-4">
                  <button
                    onClick={handleSaveParts}
                    disabled={savingParts || selectedPartNumbers.length === 0}
                    className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                  >
                    {savingParts ? 'Saving...' : 'Save Parts'}
                  </button>
                  <button
                    onClick={handleCancelEditParts}
                    disabled={savingParts}
                    className="px-4 py-2 text-sm bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                    {selectedPartNumbers.length} part(s) selected
                  </span>
                </div>
              </div>
            )}

            <table className="min-w-full print-table">
              <thead className="bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Part Number</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Description</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Received</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Condition</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {returnData.parts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No parts associated with this return.
                    </td>
                  </tr>
                ) : (
                  returnData.parts.map((part) => (
                    <tr key={part.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {part.PartNumber}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {part.Description || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {part.IsRepair ? 'Repair' : 'Return'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {part.Received ? 'Yes' : 'No'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {part.ConditionDescription || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Images Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 overflow-hidden no-print">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <ImageIcon size={18} />
                Images
              </h3>
              <label className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer flex items-center gap-2 text-sm">
                <Upload size={16} />
                {uploadingImage ? 'Uploading...' : 'Upload Image'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  className="hidden"
                />
              </label>
            </div>

            <div className="p-6">
              {loadingImages ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading images...</p>
                </div>
              ) : images.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <ImageIcon size={48} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No images uploaded yet.</p>
                  <p className="text-xs mt-1">Click "Upload Image" to add photos of the return item.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {images.map((image) => (
                    <div
                      key={image.Id}
                      className="relative group border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                    >
                      <div
                        className="relative cursor-pointer"
                        onClick={() => setSelectedImageForPreview(image)}
                      >
                        <img
                          src={returnService.getImageUrl(image.Id)}
                          alt={image.OriginalFileName}
                          className="w-full h-32 object-cover hover:opacity-90"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center pointer-events-none">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteImage(image.Id); }}
                            disabled={deletingImageId === image.Id}
                            className="opacity-0 group-hover:opacity-100 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-all pointer-events-auto"
                            title="Delete image"
                          >
                            {deletingImageId === image.Id ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <X size={16} />
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="p-2 bg-gray-50 dark:bg-gray-900/50">
                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate" title={image.OriginalFileName}>
                          {image.OriginalFileName}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {formatFileSize(image.FileSize)} · {image.UploadedBy}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Image Preview Modal */}
          {selectedImageForPreview && (
            <div
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
              onClick={() => setSelectedImageForPreview(null)}
            >
              <div className="relative max-w-4xl max-h-[90vh] m-4">
                <button
                  onClick={() => setSelectedImageForPreview(null)}
                  className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300"
                >
                  <X size={24} />
                </button>
                <img
                  src={returnService.getImageUrl(selectedImageForPreview.Id)}
                  alt={selectedImageForPreview.OriginalFileName}
                  className="max-w-full max-h-[85vh] object-contain rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="text-center mt-2 text-white text-sm">
                  {selectedImageForPreview.OriginalFileName}
                </div>
              </div>
            </div>
          )}

          {/* Comments Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 overflow-hidden comments-section">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white">Comments</h3>
            </div>

            {/* Existing Comments */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700 print-comments">
              {returnData.comments.length === 0 ? (
                <div className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No comments yet.
                </div>
              ) : (
                returnData.comments.map((comment) => (
                  <div key={comment.Id} className="px-6 py-4 print-comment">
                    {editingCommentId === comment.Id ? (
                      // Edit mode
                      <div className="no-print">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-semibold text-gray-900 dark:text-white">{comment.CreateBy}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {format(new Date(comment.CreateDate), 'M/d/yyyy h:mm a')}
                          </span>
                        </div>
                        <textarea
                          value={editedCommentText}
                          onChange={(e) => setEditedCommentText(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={handleSaveComment}
                            disabled={savingComment || !editedCommentText.trim()}
                            className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                          >
                            {savingComment ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            onClick={handleCancelEditComment}
                            disabled={savingComment}
                            className="px-3 py-1 text-sm bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View mode
                      <div>
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-semibold text-gray-900 dark:text-white">{comment.CreateBy}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {format(new Date(comment.CreateDate), 'M/d/yyyy h:mm a')}
                            </span>
                            <div className="flex gap-1 no-print">
                              <button
                                onClick={() => handleEditComment(comment)}
                                className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteComment(comment.Id)}
                                disabled={deletingCommentId === comment.Id}
                                className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-800 disabled:opacity-50"
                              >
                                {deletingCommentId === comment.Id ? '...' : 'Delete'}
                              </button>
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300">{comment.Description}</p>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Add Comment Form */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 add-comment-form no-print">
              <div className="flex gap-3">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={2}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={handleAddComment}
                  disabled={submittingComment || !newComment.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 self-end"
                >
                  {submittingComment ? 'Adding...' : 'Add Comment'}
                </button>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 print-section">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Contact Information</h3>
              {!isEditingContact && (
                <button
                  onClick={handleEditContact}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 no-print"
                >
                  Edit
                </button>
              )}
            </div>
            {isEditingContact ? (
              <div className="no-print">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                    <input
                      type="text"
                      value={editedContact.recipientName}
                      onChange={(e) => handleContactChange('recipientName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter name..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                    <input
                      type="email"
                      value={editedContact.emailAddress}
                      onChange={(e) => handleContactChange('emailAddress', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter email..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={editedContact.phoneNumber}
                      onChange={(e) => handleContactChange('phoneNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter phone..."
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveContact}
                    disabled={savingContact}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
                  >
                    {savingContact ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEditContact}
                    disabled={savingContact}
                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print-contact">
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Name:</span>
                  <span className="ml-2 text-gray-700 dark:text-gray-300">{returnData.RecipientName || 'Not provided'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Email:</span>
                  <span className="ml-2 text-gray-700 dark:text-gray-300">{returnData.EmailAddress || 'Not provided'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white">Phone:</span>
                  <span className="ml-2 text-gray-700 dark:text-gray-300">{returnData.PhoneNumber || 'Not provided'}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ship Repair/Replacement Modal */}
      {showShipModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Ship Repair/Replacement
              </h2>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Shipping Company */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Shipping Company <span className="text-red-500">*</span>
                </label>
                <select
                  value={shipFormData.shippingCompany}
                  onChange={(e) => handleShipFormChange('shippingCompany', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select shipping company...</option>
                  <option value="UPS">UPS</option>
                  <option value="FedEx">FedEx</option>
                </select>
              </div>

              {/* Tracking Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tracking Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={shipFormData.trackingNumber}
                  onChange={(e) => handleShipFormChange('trackingNumber', e.target.value)}
                  placeholder="Enter tracking number"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Comments
                </label>
                <textarea
                  value={shipFormData.comments}
                  onChange={(e) => handleShipFormChange('comments', e.target.value)}
                  placeholder="Add any additional comments..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Send Customer Email Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="sendCustomerEmail"
                  checked={shipFormData.sendCustomerEmail}
                  onChange={(e) => handleShipFormChange('sendCustomerEmail', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="sendCustomerEmail" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Send notification email to customer
                </label>
              </div>

              {!returnData?.EmailAddress && shipFormData.sendCustomerEmail && (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Note: No customer email address on file. Email will not be sent.
                </p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={handleCloseShipModal}
                disabled={submittingShipment}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitShipment}
                disabled={submittingShipment || !shipFormData.shippingCompany || !shipFormData.trackingNumber.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
              >
                {submittingShipment ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Received Modal */}
      {showReceivedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Mark Return as Received
              </h2>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Condition Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Condition of Return <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <input
                      type="radio"
                      name="receivedCondition"
                      value="passed"
                      checked={receivedCondition === 'passed'}
                      onChange={(e) => setReceivedCondition(e.target.value as 'passed' | 'not_sellable')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Passed Inspection
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Item is in good condition and can be added to stock
                      </p>
                    </div>
                  </label>
                  <label className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <input
                      type="radio"
                      name="receivedCondition"
                      value="not_sellable"
                      checked={receivedCondition === 'not_sellable'}
                      onChange={(e) => setReceivedCondition(e.target.value as 'passed' | 'not_sellable')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <div className="ml-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Not Sellable
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Item is damaged or cannot be resold
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Bin Number (shown when Passed Inspection is selected) */}
              {receivedCondition === 'passed' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Bin Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={binNumber}
                    onChange={(e) => setBinNumber(e.target.value)}
                    placeholder="Enter bin number"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    The returned part(s) will be added to the Stock table with this bin number
                  </p>
                </div>
              )}

              {/* Note (shown when Not Sellable is selected) */}
              {receivedCondition === 'not_sellable' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Note <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={notSellableNote}
                    onChange={(e) => setNotSellableNote(e.target.value)}
                    placeholder="Explain why the item is not sellable..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    This note will be added to the RGA comments
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={handleCloseReceivedModal}
                disabled={processingReceived}
                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReceived}
                disabled={
                  processingReceived ||
                  !receivedCondition ||
                  (receivedCondition === 'passed' && !binNumber.trim()) ||
                  (receivedCondition === 'not_sellable' && !notSellableNote.trim())
                }
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
              >
                {processingReceived ? 'Processing...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ReturnDetail;
